"use client";
import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { loginFormSchema, LoginFormSchemaValues } from "./login-form-schema";
import { apiPost } from "@/lib/api";
import { BASE_URL } from "@/lib/config";
import { setCookie } from "cookies-next";
import { Input } from "@/components/ui/input";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";

type LoginResponse = {
    data: {
        token: {
            access_token: string;
        };
        user_pk: number;
        name: string;
    };
};
const LoginForm = () => {
    const form = useForm<LoginFormSchemaValues>({
        resolver: zodResolver(loginFormSchema),
        defaultValues: {
            username: "",
            password: "",
        },
    });
    const [loginError, setLoginError] = useState<string | null>(null);

    const onSubmit = async (data: LoginFormSchemaValues) => {
        try {
            // setIsLoading(true);
            setLoginError(null);

            // const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
            //     data.emailOrPhone
            // );

            const response = await apiPost<LoginResponse>(
                `${BASE_URL}/general/chief-login/`,
                {
                    username: data.username,
                    password: data.password,
                }
            );

            if (response.error) {
                setLoginError(response.error);
                // setIsLoading(false);
                return;
            }

            if (response.data?.data?.token?.access_token) {
                // Set HTTP-only cookie for better security
                setCookie("auth-token", response.data.data.token.access_token, {
                    expires: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
                });

                // Keep loading state active during redirect
                window.location.href = "/home";
            } else {
                setLoginError("Invalid response from server");
                // setIsLoading(false);
            }
        } catch (error) {
            setLoginError("An error occurred during login");
            console.error("Login error:", error);
            // setIsLoading(false);
        }
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4 sm:space-y-6"
            >
                {loginError && (
                    <div className="p-3 text-sm text-red-500 bg-red-50 rounded-md">
                        {loginError}
                    </div>
                )}

                <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                        <FormItem className="relative" >
                            <FormLabel className="text-white">Username</FormLabel>
                            <FormControl>
                                <Input className="text-white h-11" placeholder="" {...field} />
                            </FormControl>
                            <FormMessage className="absolute -bottom-4 right-0 text-[0.73em]" />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                        <FormItem className="relative" >
                            <FormLabel className="text-white">Password</FormLabel>
                            <FormControl>
                                <Input className="text-white h-11" placeholder="" {...field} />
                            </FormControl>
                            <FormMessage className="absolute -bottom-4 right-0 text-[0.73em]" />
                        </FormItem>
                    )}
                />

                {/* <div className="flex items-center justify-between">
                    <a
                        href="/forget-password"
                        className="text-sm text-am-heading text-white"
                    >
                        Forgot password?
                    </a>
                </div> */}

                <Button type="submit" className="hover:bg-white hover:text-black w-full" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? <Loader2 className="animate-spin" /> : "Sign In"}
                </Button>
            </form>
        </Form>
    );
};

export default LoginForm;
